import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md'; loading?: boolean }>(
  ({ className, variant = 'secondary', size = 'md', loading, disabled, children, ...rest }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-8.5 px-3 text-[13px]',
        variant === 'primary' && 'bg-ink text-white hover:bg-ink/85',
        variant === 'secondary' && 'border border-line bg-panel text-ink hover:bg-line-2',
        variant === 'ghost' && 'text-ink-2 hover:bg-line-2 hover:text-ink',
        variant === 'danger' && 'border border-bad/30 bg-panel text-bad hover:bg-bad-soft',
        className,
      )}
      {...rest}
    >
      {loading && <Loader2 className="size-3.5 animate-spin" />}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';

const field = 'w-full rounded-md border border-line bg-panel px-2.5 text-[13px] text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 disabled:bg-line-2 disabled:text-ink-3';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...rest }, ref) => (
  <input ref={ref} className={cn(field, 'h-8.5', className)} {...rest} />
));
Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...rest }, ref) => (
  <textarea ref={ref} className={cn(field, 'py-2 leading-relaxed', className)} {...rest} />
));
Textarea.displayName = 'Textarea';

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(({ className, children, ...rest }, ref) => (
  <select ref={ref} className={cn(field, 'h-8.5 pr-7', className)} {...rest}>
    {children}
  </select>
));
Select.displayName = 'Select';

export const Label = ({ children, hint, htmlFor }: { children: ReactNode; hint?: ReactNode; htmlFor?: string }) => (
  <label htmlFor={htmlFor} className="mb-1 flex items-baseline justify-between gap-2 text-xs font-medium text-ink-2">
    <span>{children}</span>
    {hint && <span className="font-normal text-ink-3">{hint}</span>}
  </label>
);

export const Field = ({ label, hint, children, className }: { label: ReactNode; hint?: ReactNode; children: ReactNode; className?: string }) => (
  <div className={className}>
    <Label hint={hint}>{label}</Label>
    {children}
  </div>
);

type Tone = 'neutral' | 'ok' | 'warn' | 'bad' | 'brand';
export const Badge = ({ tone = 'neutral', children, className, title }: { tone?: Tone; children: ReactNode; className?: string; title?: string }) => (
  <span
    title={title}
    className={cn(
      'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium leading-none whitespace-nowrap',
      tone === 'neutral' && 'bg-line-2 text-ink-2',
      tone === 'ok' && 'bg-ok-soft text-ok',
      tone === 'warn' && 'bg-warn-soft text-warn',
      tone === 'bad' && 'bg-bad-soft text-bad',
      tone === 'brand' && 'bg-brand-soft text-brand',
      className,
    )}
  >
    {children}
  </span>
);

export const Card = ({ children, className, title, actions }: { children: ReactNode; className?: string; title?: ReactNode; actions?: ReactNode }) => (
  <section className={cn('rounded-lg border border-line bg-panel', className)}>
    {(title || actions) && (
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5">
        <h3 className="text-[13px] font-semibold">{title}</h3>
        <div className="flex items-center gap-2">{actions}</div>
      </header>
    )}
    {children}
  </section>
);

export const Spinner = ({ className }: { className?: string }) => <Loader2 className={cn('size-4 animate-spin text-ink-3', className)} />;

export const Loading = ({ label = 'Loading…' }: { label?: string }) => (
  <div className="flex items-center gap-2 p-6 text-ink-3">
    <Spinner /> {label}
  </div>
);

export const ErrorNote = ({ error }: { error: unknown }) => (
  <div className="rounded-md border border-bad/20 bg-bad-soft px-3 py-2 text-[13px] text-bad">{(error as Error)?.message ?? String(error)}</div>
);

export const Empty = ({ title, children }: { title: string; children?: ReactNode }) => (
  <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
    <p className="font-medium">{title}</p>
    {children && <div className="max-w-md text-[13px] text-ink-2">{children}</div>}
  </div>
);

export const Tabs = <T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: Array<{ value: T; label: ReactNode }> }) => (
  <div role="tablist" className="flex gap-1 border-b border-line">
    {items.map((it) => (
      <button
        key={it.value}
        role="tab"
        aria-selected={value === it.value}
        onClick={() => onChange(it.value)}
        className={cn(
          '-mb-px border-b-2 px-3 py-2 text-[13px] font-medium transition-colors',
          value === it.value ? 'border-ink text-ink' : 'border-transparent text-ink-2 hover:text-ink',
        )}
      >
        {it.label}
      </button>
    ))}
  </div>
);

export const Toggle = ({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; disabled?: boolean }) => (
  <label className={cn('inline-flex cursor-pointer items-center gap-2 text-[13px]', disabled && 'cursor-not-allowed opacity-50')}>
    <input type="checkbox" className="size-3.5 accent-ink" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

export const Dialog = ({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; wide?: boolean }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/30 p-4 pt-[10vh]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal className={cn('w-full rounded-lg border border-line bg-panel shadow-xl', wide ? 'max-w-3xl' : 'max-w-lg')}>
        <header className="border-b border-line px-5 py-3 text-[15px] font-semibold">{title}</header>
        <div className="px-5 py-4">{children}</div>
        {footer && <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">{footer}</footer>}
      </div>
    </div>
  );
};

export const Stat = ({ label, value, tone }: { label: string; value: ReactNode; tone?: 'bad' }) => (
  <div className="min-w-0">
    <div className="text-[11px] font-medium tracking-wide text-ink-3 uppercase">{label}</div>
    <div className={cn('mt-0.5 truncate text-[15px] font-semibold tabular-nums', tone === 'bad' && 'text-bad')}>{value}</div>
  </div>
);
